import { conversationContextSchema } from "@do/shared-types";
import type { NextRouter } from "next/router";
import { z } from "zod";

/**
 * This hook returns query params used to setup the chatbot.
 * - `callingURL`: used to set the conversation starter intent according to the calling URL (url of the page where the chatbot is integrated)
 * - `chatbotContext`: used to set the conversation starter intent based on the id of the button clicked to open the chatbot.
 * - `dochatConfigId` and `sessionId`: used to retrieve related dochat parameters in order to start their chatbot.
 * - `dynamicContext`: Used to pass a dynamic context to the chatbot from a widget integration or directly from an iframe integration passed to do bot in metadata.dynamicContext
 * - `hasNoHeader`: if true, the header including the close button is not rendered
 * - `isTestingMode`: if true, the chatbot is rendered in testing mode with edition button in text bubbles
 * - `pinCode`: used by do-bot to retrieve a dynamic context created in contrack, this used for a SMS to BOT integration through voice-api passed to do-bot in metadata.pinCode
 * - `positionId`: id of a voice position, used to link a static context to a voicePositionId passed to do-bot in metadata.staticContext
 * - `simDatetime`: moment the console testing page plays the conversation at, ISO with or without offset (Paris time without), passed to do-bot in metadata.simDatetime. Testing mode only
 * - `theming`: key of the theming CSS config to use. Used for ICP integration
 */

const queryParamsSchema = z.object({
  brand: z.string().optional(),
  callingURL: z.string().optional(),
  isClosable: z.boolean().optional(),
  chatbotStaticCtxMapKey: z.string().optional(),
  dochatConfigId: z.string(),
  digitalDynamicCtx: conversationContextSchema.optional(),
  hasNoHeader: z.boolean().optional(),
  isTestingMode: z.boolean().optional(),
  isEditingMode: z.boolean().optional(),
  isUrlRewriteDebug: z.boolean().optional(),
  localeFromQueryParams: z.string().optional(),
  pinCode: z.string().optional(),
  positionId: z.string().optional(),
  sessionId: z.string().optional(),
  simDatetime: z.string().optional(),
  theming: z.string().optional(),
});

/** Without an offset, the chatbot reads the moment in Paris time, like the timetables. */
const simDatetimeSchema = z.string().datetime({ local: true, offset: true });

const removeArraysFromObjectValues = (obj: Record<string, unknown>) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      // eslint-disable-next-line no-console
      console.warn("Array found in query param, the value is discarded", key, value);
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {} as Record<string, unknown>);
};
const parseAndDecodeQueryParam = (
  queryParam: string | string[] | undefined
): Record<string, string> | null => {
  if (!queryParam || typeof queryParam != "string") return null;
  try {
    const value = JSON.parse(decodeURIComponent(queryParam));
    if (typeof value === "object") return value;
    // eslint-disable-next-line no-console
    console.warn("Query param is not an object", value);
    return null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Error while decoding and parsing object", e);
    return null;
  }
};

export const getQueryParams = (router: NextRouter) => {
  const brand = router.query.brand;
  const callingURL = router.query["calling-url"];
  const chatbotStaticCtxMapKey = router.query["chatbot-context"];
  const isClosable = "isClosable" in router.query;
  const dochatConfigId = router.query.dochatConfigId;
  const dynamicContextStr = router.query["dynamic-context"];
  const hasNoHeader = "no-header" in router.query;
  const isTestingMode = "is-testing-mode" in router.query;
  const isEditingMode = "is-editing-mode" in router.query;
  const isUrlRewriteDebug = "dialonce-debug" in router.query;
  const localeFromQueryParams = router.query.locale;
  const pinCode = router.query.pinCode;
  const positionId = router.query.positionId;
  const sessionId = router.query.sessionId;
  const simDatetimeStr = router.query["sim-datetime"];
  const theming = router.query.theming;

  const parsedDynamicCtx = parseAndDecodeQueryParam(dynamicContextStr);
  const dynamicContextValidated = conversationContextSchema.safeParse(parsedDynamicCtx);

  // Only the console testing page simulates a moment (DAS-2743): elsewhere the param is ignored.
  const simDatetimeValidated = simDatetimeSchema.safeParse(simDatetimeStr);
  if (simDatetimeStr !== undefined && !simDatetimeValidated.success) {
    // eslint-disable-next-line no-console
    console.warn("Invalid sim-datetime query param, the value is discarded", simDatetimeStr);
  }

  const queryParamsWithoutArrays = removeArraysFromObjectValues({
    brand,
    callingURL,
    chatbotStaticCtxMapKey,
    isClosable,
    dochatConfigId,
    digitalDynamicCtx: dynamicContextValidated.success ? dynamicContextValidated.data : undefined,
    hasNoHeader,
    isTestingMode,
    isEditingMode,
    isUrlRewriteDebug,
    localeFromQueryParams,
    pinCode,
    positionId,
    sessionId,
    simDatetime:
      isTestingMode && simDatetimeValidated.success ? simDatetimeValidated.data : undefined,
    theming,
  });
  const validatedQueryParams = queryParamsSchema.parse(queryParamsWithoutArrays);

  return validatedQueryParams;
};
